<template>
    <div class="bodyheight grid bg-gray-100 dark:bg-black relative">
        <bg-global :bgLobby="bgLobby" />

        <ul class="bodyheight overflow-auto flex flex-col gap-1 py-2 text-black dark:text-gray-100 z-10">
            <div class="mb-2 flex items-center justify-center gap-2">
                <span class="uppercase font-semibold">Your Games</span>
                <button v-if="isTeamWallet" @click.prevent="downloadGames" :disabled="downloading" class="bg-gray-800 text-white rounded-full flex items-center justify-center h-6 w-6 disabled:opacity-50">
                    <i class="fas fa-download text-xs"></i>
                </button>
            </div>

            <div
                v-if="isTeamWallet"
                class="text-white uppercase text-xs flex inline-flex items-center justify-center gap-2 mb-2">
                Show All Games?
                <input @click.stop="showAllGames" :checked="show_all" type="checkbox">
            </div>

            <li v-for="(game, index) in games" class="mx-2"
                :class="index % 2 == 0 ? 'bg-black bg-opacity-10 dark:bg-white dark:bg-opacity-10' : 'dark:bg-white dark:bg-opacity-5'">
                <div class="flex flex-col p-2 rounded text-sm gap-1"
                    @click="selectGame(game)">
                    <div class="flex items-center justify-between gap-1">
                        <div class="text-sm text-gray-700 dark:text-inner-border">{{ toDate(game.ended_at) }}</div>
                        <div class="text-sm text-gray-700 dark:text-white uppercase font-semibold">{{ game.status }}</div>
                    </div>
                    <div class="flex items-center justify-between gap-1">
                        <div class="flex items-center gap-1">
                            <span class="text-xs text-gray-500 dark:text-gray-100" v-text="game.is_private ? (game.is_special ? 'Special: ' : 'Private: ') : 'Lobby:'"></span>
                            <span>{{ game.name !== null ? game.name : game.template?.name }}</span>
                            <span v-if="game.is_private">({{ game?.template?.name }})</span>
                            <span v-if="game.is_private && !game.is_special"><i class="fas fa-lock text-xs"></i></span>
                        </div>
                        <div class="flex items-center justify-end gap-1" v-if="you(game?.players[0]?.user) && !game.is_special">
                            <span class="text-xs text-gray-500 dark:text-green-500 uppercase font-semibold">Win</span>
                            <span class="text-sm font-semibold text-white">{{ prize(game.template?.entry,
                                game.template?.max_players) }}</span>
                            <img src="/images/sol.png" class="block h-4 w-4 rounded-full" />
                            <span class="flex items-center gap-1" v-if="game.token_awarded">
                                <span class="text-xs font-mono">+</span>
                                <img :src="rrTokenImage(game.template.token)" class="block h-5 w-5 rounded-full" />
                            </span>
                        </div>
                    </div>
                    <div class="flex items-center justify-between gap-1">
                        <div class="flex items-center gap-1" @click.stop="more(game.id)">
                            <img src="/images/checkered-flags.svg" class="h-8 w-8 block" v-if="game.status === 'ended'">
                            <div v-for="(player, pos) in game.players.slice(0, 4)">
                                <img :src="player.user.pfp" class="block rounded-full object-cover border-2"
                                    :class="[you(player.user) ? 'border-green-500 h-8 w-8' : 'border-white h-6 w-6']"
                                    v-if="player.user.pfp" />
                                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                                    class="w-6 h-6">
                                    <path fill-rule="evenodd"
                                        d="M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 015.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
                                        clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div v-if="game.status === 'ended'"
                                class="bg-black/50 rounded-full w-6 h-6 flex items-center justify-center">
                                <i class="fas fa-ellipsis text-white"></i>
                            </div>
                        </div>
                        <div v-if="game.turns" class="flex items-center gap-2">
                            <span>Turns:</span>
                            <span class="font-semibold"
                                :class="(jackpot.turns > 0 && game.turns <= jackpot.turns) ? 'text-green-500' : 'text-inner-border'">{{
                                    game.turns }}</span>
                        </div>
                    </div>
                    <div v-if="showMore === game.id && game.status === 'ended'" class="bg-black/50 p-2 rounded relative">
                        <div v-if="this.gameToShow !== null">
                            <div v-for="player in this.gameToShow.players" class="flex items-center gap-2 font-mono text-xs">
                                <span>{{ getOrdinalSuffix(player.place) }}</span>
                                <span> - {{ player.user.short_wallet }}</span>
                                <span>{{ player.user.name !== player.user.short_wallet ? ' - ' + player.user.name : '' }}</span>
                            </div>
                        </div>
                        <div class="absolute top-2 right-4 text-white text-lg" @click.stop="copy(game.id)">
                            <i class="far fa-copy"></i>
                        </div>
                    </div>
                </div>
            </li>
            <li v-if="!games.length" class="flex items-center justify-center text-sm text-red-400 dark:text-red-200 italic">
                No games yet!
            </li>
            <li class="flex items-center justify-center my-2">
                <button
                    class="disabled:opacity-75 py-1 px-2 text-xs uppercase rounded drop-shadow bg-gray-400 text-gray-100 dark:bg-jackpot-bg dark:text-white flex items-center gap-2"
                    :disabled="fetching" @click="loadMore" v-if="next_page <= last_page" v-cloak>
                    <span>Load More</span>
                    <spinner v-if="fetching" />
                </button>
            </li>
        </ul>
    </div>
</template>

<script>
import * as dayjs from 'dayjs'
import solana from '../mixins/solana'
import { useWallet } from 'solana-wallets-vue'

export default {
    name: 'YourGamesList',
    mixins: [solana],
    props: ['token', 'games', 'fetching', 'next_page', 'last_page', 'bgLobby', 'jackpot', 'tokens', 'isTeamWallet', 'show_all'],
    data() {
        return {
            downloading: false,
            showMore: null,
            gameToShow: null,
        }
    },
    watch: {
        showMore(value) {
            this.gameToShow = null
            if (value !== null) {
                this.gameToShow = this.games.find(obj => obj.id === value)
            }
        }
    },
    methods: {
        showAllGames() {
            this.emitter.emit('show-all-games-list-toggle')
        },
        you(user) {
            return user?.wallet == useWallet().publicKey.value.toString()
        },
        loadMore() {
            this.emitter.emit('load-more-games-list')
        },
        toDate(date) {
            if (date === null) {
                return '-';
            }
            return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
        },
        prize(entry, players) {
            return this.lamportsToSol(entry * players)
        },
        selectGame(game) {
            if (game.status !== 'ended' && game.status !== 'running') {
                return
            }
            this.emitter.emit('select-game', { game_id: game.id, game_template_id: game.game_template_id, players: game.players, status: game.status })
        },
        more(id) {
            if (this.showMore === id) {
                this.showMore = null
            } else {
                this.showMore = id
            }
        },
        async copy(id) {
            const game = this.games.find(obj => obj.id === id)
            const text = game.players.map((item) => {
                return item.place + ';' + item.user.wallet + ';' + item.user.name
            }).join('\n')
            try {
                await this.copyToClipboard(text)
                this.$toast.success('Copied to clipboard!')
            } catch (err) {
                this.$toast.error('Failed to copy to clipboard!')
            }
        },
        rrTokenImage(token) {
            const found = this.tokens.find(item => item.slug === token)
            if (found === undefined) {
                return null;
            }
            return found.image
        },
        async downloadGames() {
            this.downloading = true
            await axios.post('/api/admin/games/csv', {}, {
                headers: {
                    'Authorization': `Bearer ${this.token}`
                }
            }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'games.csv');
                document.body.appendChild(link);
                link.click();
            }).finally(() => {
                this.downloading = false
            })
        }
    }
}
</script>
